<template>
  <DialogContainer
    :isOpen="isOpen"
    :onClose="onClose"
    :maxWidth="'2xl'"
    :hasCloseBtn="true"
  >
    <template v-slot:title>Erasure record</template>
    <template v-slot:content>
      <div class="mt-4 text-sm text-gray-500">
        <p class="font-medium text-gray-700">{{ email }}</p>

        <p v-if="loading" class="mt-3">Loading…</p>

        <div v-else-if="record" class="mt-3 space-y-1">
          <p>
            <span class="font-medium">Status:</span>
            <span :class="statusClass">{{ record.status }}</span>
          </p>
          <p>
            <span class="font-medium">Requested by:</span>
            {{ record.requested_by_email }}
          </p>
          <p>
            <span class="font-medium">Requested at:</span>
            {{ formattedDate(record.requested_at) }}
          </p>
          <p>
            <span class="font-medium">Purge started at:</span>
            {{ formattedDate(record.started_at) || "Not started" }}
          </p>
          <p>
            <span class="font-medium">Completed at:</span>
            {{ formattedDate(record.completed_at) || "Not finished" }}
          </p>
          <p>
            <span class="font-medium">Reason:</span> {{ record.reason || "—" }}
          </p>
          <p>
            <span class="font-medium">Users erased:</span>
            {{ cascadedUsers }}
          </p>

          <table class="mt-4 w-full text-left table-auto">
            <thead class="border-b">
              <tr>
                <th class="py-2 text-xs uppercase tracking-wider">Step</th>
                <th class="py-2 text-xs uppercase tracking-wider">Outcome</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(outcome, step) in record.steps"
                :key="step"
                class="border-t"
              >
                <td class="py-2">{{ step }}</td>
                <td
                  class="py-2"
                  :class="outcome.ok ? 'text-green-600' : 'text-red-600'"
                >
                  {{ outcome.ok ? "ok" : stepErrors(outcome) }}
                </td>
              </tr>
            </tbody>
          </table>
          <p v-if="!hasSteps" class="mt-4 text-gray-400">
            The purge has not reported any steps yet.
          </p>

          <p v-if="record.status === 'PARTIAL'" class="mt-4 text-red-600">
            At least one step failed, so the account is not marked erased.
            Retrying from the Erasure tab resumes this same record; the failed
            steps above stay on it as the trail of what went wrong.
          </p>
        </div>

        <p v-else class="mt-3">
          No erasure record is filed against this account. If it shows as
          erased, it was erased as part of another account in its organization —
          open that account to see the record.
        </p>
      </div>
    </template>
  </DialogContainer>
</template>

<script>
import { ref } from "vue";
import DialogContainer from "@/components/common/DialogContainer";
import formMixin from "@/mixins/formMixin";
import eventBus from "@/utils/eventBus";
import { ERASURE_RECORD_EVENT } from "@/components/admin/user-page/modals/erasureEvents";

export default {
  name: "ErasureRecordModal",
  mixins: [formMixin],
  components: {
    DialogContainer,
  },
  beforeMount() {
    eventBus.on(ERASURE_RECORD_EVENT, this.open);
  },
  beforeUnmount() {
    eventBus.off(ERASURE_RECORD_EVENT, this.open);
  },
  setup() {
    return {
      isOpen: ref(false),
      loading: ref(false),
      email: ref(""),
      record: ref(null),
    };
  },
  computed: {
    cascadedUsers() {
      const ids = (this.record && this.record.cascaded_user_ids) || [];
      return ids.length ? ids.join(", ") : "—";
    },
    hasSteps() {
      return Object.keys((this.record && this.record.steps) || {}).length > 0;
    },
    statusClass() {
      const status = this.record && this.record.status;
      if (status === "COMPLETED") {
        return "text-green-600";
      }
      if (status === "PARTIAL") {
        return "text-red-600";
      }
      if (status === "IN_PROGRESS" || status === "PENDING") {
        return "text-yellow-700";
      }
      return "";
    },
  },
  methods: {
    // run_purge records a failure per user - {ok: false, detail: {"12": {error}}}
    // - so there is no top-level `error` to print. Flatten the users that
    // actually failed; the rest of the org succeeded and has nothing to say
    // here. Without this the Outcome cell on a PARTIAL record is blank, which
    // is exactly the record an admin opens this modal to read.
    stepErrors(outcome) {
      const detail = (outcome && outcome.detail) || {};
      const failures = Object.entries(detail)
        .filter(([, result]) => result && result.error)
        .map(([userId, result]) => `user ${userId}: ${result.error}`);
      return failures.length ? failures.join("; ") : "failed";
    },
    formattedDate(timestamp) {
      return timestamp ? new Date(timestamp).toLocaleString("en-US") : null;
    },
    async open({ userId, email }) {
      this.email = email;
      this.record = null;
      this.isOpen = true;
      this.loading = true;
      try {
        const response = await this.handleSubmissionErrors(
          () => this.$alfredService.adminGetUserErasures(userId),
          {}
        );
        // At most one row per user: the endpoint refuses to open a second
        // request while one is unfinished, and a COMPLETED one sets
        // data_purged_at, which it also refuses on. A retry resumes the row it
        // is given rather than filing another, so `results[0]` is the record.
        const results = (response && response.results) || [];
        this.record = results[0] || null;
      } finally {
        this.loading = false;
      }
    },
    onClose() {
      this.isOpen = false;
    },
  },
};
</script>
